import { X } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { DeleteIcon } from '@/components/icons'
import { useDeleteTestSuite } from '@/hooks'
import type { TestSuite } from '@/types'

interface DeleteTestSuiteDialogProps {
  projectId: string
  testSuite: TestSuite
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteTestSuiteDialog({ projectId, testSuite, open, onOpenChange }: DeleteTestSuiteDialogProps) {
  const deleteTestSuite = useDeleteTestSuite(projectId)

  const handleDelete = async () => {
    try {
      await deleteTestSuite.mutateAsync(testSuite.id)
      toast.success(`${testSuite.title} deleted`)
      onOpenChange(false)
    } catch {
      toast.error('Failed to delete test suite')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-lg" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-primary">Delete Test Suite?</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-primary">
          Are you sure you want to delete the test suite "<span className="font-bold">{testSuite.title} ({testSuite.testCaseCount} Test {testSuite.testCaseCount === 1 ? 'Case' : 'Cases'})</span>"?
          This action cannot be undone and the test suite and its test cases will be permanently removed from the list.
        </DialogDescription>
        <DialogFooter>
          <DialogClose asChild>
            <Button
              variant="ghost"
              disabled={deleteTestSuite.isPending}
              className="h-10 px-4 font-bold"
              data-testid={`cancel-delete-test-suite-${testSuite.testSuiteIdentifier}`}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteTestSuite.isPending}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6"
            data-testid={`confirm-delete-test-suite-${testSuite.testSuiteIdentifier}`}
          >
            <DeleteIcon className="h-6 w-6" />
            {deleteTestSuite.isPending ? 'Deleting...' : 'Delete Test Suite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
